'use client';

import UiIcon from '@/components/ui/UiIcon';
import { memo, useCallback, useRef, useMemo, useState } from 'react';
import MessageBubble from './MessageBubble';
import type { DirectMessage } from '@/app/messages/_types';

// Комментарий на русском: высота видимой "шапки" карточки (первая строка текста).
const CARD_PEEK_HEIGHT = 32; // px
const MAX_VISIBLE_CARDS = 4; // Максимум видимых карточек в стопке

// Комментарий на русском: преобразование hex в RGB triplet для CSS-переменных.
const hexToTriplet = (hex: unknown): string => {
  const s = String(hex || '').trim().toLowerCase();
  if (!/^#[0-9a-f]{6}$/.test(s)) return '';
  const r = parseInt(s.slice(1, 3), 16) || 0;
  const g = parseInt(s.slice(3, 5), 16) || 0;
  const b = parseInt(s.slice(5, 7), 16) || 0;
  return `${r} ${g} ${b}`;
};

// Комментарий на русском: выбираем контрастный цвет текста (белый/тёмный) под фон.
const contrastTextTriplet = (rgbTriplet: string): string => {
  const [r, g, b] = rgbTriplet.split(/\s+/).map((v) => Number(v));
  const rr = Number.isFinite(r) ? r : 0;
  const gg = Number.isFinite(g) ? g : 0;
  const bb = Number.isFinite(b) ? b : 0;
  // Комментарий на русском: быстрый порог по яркости (YIQ).
  const yiq = (rr * 299 + gg * 587 + bb * 114) / 1000;
  return yiq >= 160 ? '15 23 42' : '255 255 255';
};

interface StackBubble3DProps {
  messages: DirectMessage[];
  isOwn: boolean;
  showAvatar?: boolean;
  peerOnline?: boolean;
  stackId: string;
  isActiveRun: boolean;
  owner: 'me' | 'other';
  mode?: 'collapsed' | 'expanded' | 'merged';
  carouselIndex?: number;
  deferHeavy?: boolean;
  // Комментарий на русском: ID чата для постоянного кэширования фото.
  chatId?: string;
  onExpand?: () => void;
  onCollapse?: () => void;
  onMerge?: () => void;
  onOpenMenu?: () => void;
  onOpenPhotoViewer?: (photoUrl: string, messageId: number) => void;
  onDelete?: (msg: DirectMessage) => void;
  onReply?: (msg: DirectMessage) => void;
  onReact?: (msgId: number, emoji: string) => void;
  onToggleSelect?: (msg: DirectMessage) => void;
  onForward?: (msg: DirectMessage) => void;
  onEdit?: (msg: DirectMessage) => void;
  onPin?: (msg: DirectMessage) => void;
  selectionMode?: boolean;
  selectedMsgKeySet?: Set<string>;
  isPinned?: boolean;
  linkify?: boolean;
  enableLinkPreview?: boolean;
}

// Комментарий на русском: стопка карточек — нижние выглядывают сверху (видна первая строка).
const StackBubble3D = memo(function StackBubble3D(props: StackBubble3DProps) {
  const {
    messages,
    isOwn,
    showAvatar,
    peerOnline,
    stackId,
    isActiveRun,
    mode = 'collapsed',
    deferHeavy = false,
    chatId = '',
    onExpand,
    onCollapse,
    onOpenMenu,
    onOpenPhotoViewer,
    onDelete,
    onReply,
    onReact,
    onToggleSelect,
    onForward,
    onEdit,
    onPin,
    selectionMode,
    selectedMsgKeySet,
    isPinned,
    linkify = true,
    enableLinkPreview = true,
  } = props;

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Комментарий на русском: какой peek-card сейчас "раскрыт" (hover/tap).
  const [expandedPeekId, setExpandedPeekId] = useState<number | null>(null);

  // Комментарий на русском: ВСЕ хуки ДОЛЖНЫ быть ДО любых условных return!
  const visibleMessages = useMemo(() => {
    if (messages.length <= MAX_VISIBLE_CARDS) return messages;
    return [...messages.slice(0, MAX_VISIBLE_CARDS - 1), messages[messages.length - 1]];
  }, [messages]);

  const hiddenCount = useMemo(() => Math.max(0, messages.length - MAX_VISIBLE_CARDS), [messages.length]);
  const peekCount = visibleMessages.length - 1;

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (mode !== 'collapsed') return;
    touchStartRef.current = { x: e.clientX, y: e.clientY };
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      touchStartRef.current = null;
      onOpenMenu?.();
    }, 400);
  }, [mode, onOpenMenu]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!longPressTimerRef.current || !touchStartRef.current) return;
    const dx = e.clientX - touchStartRef.current.x;
    const dy = e.clientY - touchStartRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > 8) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      touchStartRef.current = null;
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  }, []);

  // Комментарий на русском: клик по верхней карточке — раскрыть весь стэк.
  const handleTopCardClick = useCallback((e: React.MouseEvent) => {
    if (mode !== 'collapsed') return;
    e.preventDefault();
    e.stopPropagation();
    onExpand?.();
  }, [mode, onExpand]);

  // Комментарий на русском: клик по peek-карточке — toggle раскрытия (на сенсорных).
  const handlePeekClick = useCallback((e: React.MouseEvent, msgId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedPeekId((prev) => (prev === msgId ? null : msgId));
  }, []);

  // Комментарий на русском: режим "merged".
  if (mode === 'merged') {
    const mergedText = messages.map((m) => m.message || '').join('\n\n');
    const firstMsg = messages[0];
    const lastMsg = messages[messages.length - 1];

    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className="stack-merged-wrapper">
          <MessageBubble
            message={{ ...firstMsg, message: mergedText, created_at: lastMsg.created_at } as any}
            isOwn={isOwn}
            showAvatar={showAvatar}
            peerOnline={peerOnline}
            isGrouped={false}
            chatId={chatId}
            onOpenPhotoViewer={onOpenPhotoViewer}
            onDelete={onDelete as any}
            onReply={onReply as any}
            onReact={onReact}
            selectionMode={selectionMode}
            isSelected={false}
            onToggleSelect={onToggleSelect as any}
            onForward={onForward as any}
            onEdit={onEdit as any}
            onPin={onPin as any}
            isPinned={isPinned}
            linkify={linkify}
            enableLinkPreview={enableLinkPreview}
          />
          <button
            type="button"
            onClick={() => onCollapse?.()}
            className={`mt-1 text-[10px] text-site-muted hover:text-site-text transition ${isOwn ? 'text-right' : 'text-left'} block w-full`}
          >
            <UiIcon name="Scissors" /> Разделить
          </button>
        </div>
      </div>
    );
  }

  // Комментарий на русском: режим "expanded".
  if (mode === 'expanded') {
    return (
      <div className="stack-expanded-wrapper">
        <div className={`flex items-center gap-2 mb-2 px-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className="text-[10px] text-site-muted bg-site-surface/50 px-2 py-0.5 rounded-full">
            <UiIcon name="BookOpen" /> {messages.length} сообщ.
          </span>
          <button
            type="button"
            onClick={() => onCollapse?.()}
            className="text-[10px] text-site-accent hover:text-site-accent transition"
          >
            Свернуть ↩
          </button>
        </div>
        <div className="space-y-0.5">
          {messages.map((msg, idx) => (
            <MessageBubble
              key={msg.id}
              message={msg as any}
              isOwn={isOwn}
              showAvatar={showAvatar && idx === 0}
              peerOnline={peerOnline}
              isGrouped={idx > 0}
              chatId={chatId}
              onOpenPhotoViewer={onOpenPhotoViewer}
              onDelete={onDelete as any}
              onReply={onReply as any}
              onReact={onReact}
              selectionMode={selectionMode}
              isSelected={!!selectedMsgKeySet?.has(`m:${msg.id}`)}
              onToggleSelect={onToggleSelect as any}
              onForward={onForward as any}
              onEdit={onEdit as any}
              onPin={onPin as any}
              isPinned={isPinned}
              linkify={linkify}
              enableLinkPreview={enableLinkPreview}
            />
          ))}
        </div>
      </div>
    );
  }

  // Комментарий на русском: режим "collapsed" — стопка карточек.
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`stack-cards-wrapper ${isActiveRun ? 'stack-active-run' : ''}`}
        style={{
          paddingTop: peekCount > 0 ? `${peekCount * CARD_PEEK_HEIGHT}px` : 0,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {/* Комментарий на русском: выглядывающие карточки (сверху, под верхней) */}
        {visibleMessages.slice(0, -1).map((msg, idx) => {
          const peekIdx = idx;
          const topOffset = peekIdx * CARD_PEEK_HEIGHT;
          // Комментарий на русском: смещение внутрь чата — свои влево (-), чужие вправо (+).
          const xShift = isOwn ? -(peekIdx + 1) * 6 : (peekIdx + 1) * 6;
          const peekColorClass = isOwn ? 'stack-peek-card--me' : 'stack-peek-card--peer';
          const isExpanded = expandedPeekId === msg.id;
          const isInactive = expandedPeekId !== null && !isExpanded;
          const msgText = msg.message || '';

          // Комментарий на русском: в комнатах для peer-карточек применяем bubble_color автора.
          const peerTriplet = (() => {
            if (isOwn) return undefined;
            if (!chatId.startsWith('room:')) return undefined;
            const triplet = hexToTriplet((msg as any)?.from_user?.bubble_color);
            return triplet || undefined;
          })();
          const peerColorStyle: React.CSSProperties | undefined = peerTriplet
            ? ({ ['--mb-peer-rgb' as any]: peerTriplet } as any)
            : undefined;
          const bgTriplet = isOwn ? '125 211 252' : (peerTriplet || '62 99 136');

          return (
            <div
              key={msg.id}
              className={`stack-peek-card ${peekColorClass} ${isExpanded ? 'stack-peek-card--expanded' : ''} ${isInactive ? 'stack-peek-card--inactive' : ''}`}
              style={{
                // Комментарий на русском: не меняем top при раскрытии — анимируем transform, чтобы не было \"прыжка\".
                top: `${topOffset}px`,
                ['--peek-top' as any]: `${topOffset}px`,
                ['--peek-x' as any]: `${xShift}px`,
                ['--stack-peek-text-rgb' as any]: contrastTextTriplet(bgTriplet),
                zIndex: isExpanded ? 100 : peekIdx,
                ...peerColorStyle,
              }}
              onMouseEnter={() => setExpandedPeekId(msg.id)}
              onMouseLeave={() => setExpandedPeekId(null)}
              onClick={(e) => handlePeekClick(e, msg.id)}
            >
              <div className={`stack-peek-content ${isExpanded ? 'stack-peek-content--expanded' : ''}`}>
                <span className="stack-peek-text">
                  {isExpanded ? msgText : <>{msgText.split('\n')[0].slice(0, 50) || <UiIcon name="Paperclip" title="Вложение" />}{msgText.length > 50 ? '…' : ''}</>}
                </span>
              </div>
            </div>
          );
        })}

        {/* Комментарий на русском: верхняя карточка (последнее сообщение) — полностью видна */}
        <div className="stack-top-card" onClick={handleTopCardClick}>
          <MessageBubble
            message={visibleMessages[visibleMessages.length - 1] as any}
            isOwn={isOwn}
            showAvatar={showAvatar}
            peerOnline={peerOnline}
            isGrouped={false}
            chatId={chatId}
            onOpenPhotoViewer={onOpenPhotoViewer}
            onDelete={onDelete as any}
            onReply={onReply as any}
            onReact={onReact}
            selectionMode={selectionMode}
            isSelected={!!selectedMsgKeySet?.has(`m:${visibleMessages[visibleMessages.length - 1].id}`)}
            onToggleSelect={onToggleSelect as any}
            onForward={onForward as any}
            onEdit={onEdit as any}
            onPin={onPin as any}
            isPinned={isPinned}
            linkify={linkify}
            enableLinkPreview={enableLinkPreview}
          />
        </div>

        {/* Комментарий на русском: бейдж количества */}
        {messages.length > 1 && (
          <div className={`stack-count-badge ${isOwn ? 'stack-count-left' : 'stack-count-right'}`}>
            {messages.length}
            {hiddenCount > 0 && <span className="opacity-60 ml-0.5">+{hiddenCount}</span>}
          </div>
        )}

        {/* Комментарий на русском: индикатор активного рана */}
        {isActiveRun && (
          <div className={`stack-active-indicator ${isOwn ? 'stack-indicator-left' : 'stack-indicator-right'}`}>
            <UiIcon name="Clock" />
          </div>
        )}
      </div>
    </div>
  );
});

export default StackBubble3D;
